import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, ShieldCheck } from "lucide-react";
import { FluxMark } from "../components/flux/Decor";
import { useAuth, API } from "../context/AuthContext";
import TemplateManager from "../components/admin/TemplateManager";

const display = "font-display";

const PLAN_STYLE = {
  admin: "text-[#f59e0b] bg-[#f59e0b]/10 border-[#f59e0b]/30",
  unlimited: "text-[#2dd4bf] bg-[#2dd4bf]/10 border-[#2dd4bf]/30",
  weekly: "text-emerald-400 bg-emerald-400/10 border-emerald-400/30",
  trial: "text-blue-400 bg-blue-400/10 border-blue-400/30",
  expired: "text-rose-400 bg-rose-400/10 border-rose-400/30",
};

export default function Admin() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && user.role !== "admin") {
      navigate("/matrix");
      return;
    }
    axios.get(`${API}/admin/users`)
      .then((res) => setUsers(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  return (
    <div className="min-h-screen bg-[#0f1729] text-gray-100" data-testid="admin-page">
      <nav className="border-b border-white/5 px-6 py-4">
        <div className="max-w-6xl mx-auto flex items-center gap-3">
          <Link to="/matrix" data-testid="admin-back-link" className="text-gray-400 hover:text-[#f59e0b] transition-colors duration-200">
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <FluxMark className="w-7 h-7" />
          <span className={`${display} font-bold text-lg tracking-wider uppercase text-white`}>Command Center</span>
          <span className="flex items-center gap-1 text-[10px] font-mono text-[#f59e0b] bg-[#f59e0b]/10 border border-[#f59e0b]/20 px-2 py-1 rounded-lg ml-2">
            <ShieldCheck className="w-3 h-3" /> ADMIN
          </span>
        </div>
      </nav>

      <main className="max-w-6xl mx-auto px-6 py-10">
        <h1 className={`${display} text-2xl font-bold text-white mb-6`}>All Time Travelers ({users.length})</h1>
        <div className="bg-[#131f3a] border border-white/5 rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-white/5 text-left">
                <th className={`${display} text-[10px] uppercase tracking-wider text-gray-400 px-5 py-3`}>User</th>
                <th className={`${display} text-[10px] uppercase tracking-wider text-gray-400 px-5 py-3`}>Plan</th>
                <th className={`${display} text-[10px] uppercase tracking-wider text-gray-400 px-5 py-3 text-right`}>Events</th>
                <th className={`${display} text-[10px] uppercase tracking-wider text-gray-400 px-5 py-3 text-right`}>Files</th>
                <th className={`${display} text-[10px] uppercase tracking-wider text-gray-400 px-5 py-3`}>Joined</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan="5" className="px-5 py-10 text-center text-gray-400 text-xs font-mono animate-blink">Scanning the multiverse...</td></tr>
              ) : users.map((u) => (
                <tr key={u.user_id} className="border-b border-white/[0.03] hover:bg-white/[0.02]" data-testid={`admin-user-row-${u.user_id}`}>
                  <td className="px-5 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture ? (
                        <img src={u.picture} alt="" className="w-7 h-7 rounded-full" />
                      ) : (
                        <div className="w-7 h-7 rounded-full bg-[#f59e0b]/20 flex items-center justify-center text-[#f59e0b] text-xs font-bold">{(u.name || u.email)[0].toUpperCase()}</div>
                      )}
                      <div>
                        <div className="text-white text-xs font-semibold">{u.name || "—"}</div>
                        <div className="text-gray-400 text-[11px]">{u.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-5 py-3">
                    <span className={`text-[10px] font-mono font-bold uppercase px-2 py-0.5 rounded border ${PLAN_STYLE[u.access.plan] || PLAN_STYLE.trial}`}>
                      {u.access.plan}{u.access.days_left != null ? ` · ${u.access.days_left}d` : ""}
                    </span>
                  </td>
                  <td className="px-5 py-3 text-right font-mono text-gray-300">{u.event_count}</td>
                  <td className="px-5 py-3 text-right font-mono text-gray-300">{u.file_count}</td>
                  <td className="px-5 py-3 text-gray-400 text-[11px] font-mono">{u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <TemplateManager />
      </main>
    </div>
  );
}
